// IndexedDB storage layer for the offline queue.
//
// Extracted from offlineQueue.ts (Phase 3b, step 4). Owns the IDB connection,
// the single 'queue' object store, and the generic read/write/failure ops over
// queue entries. The per-entity insert/enqueue logic + the drainer stay in
// offlineQueue.ts; so do discard/retry (operator actions that emit change
// events / kick a drain). Depends only on the entry types.

import type { Entry } from '@/lib/offlineQueue/types'
import { requestPersistenceOnce } from '@/lib/persistentStorage'

const DB_NAME    = 'kf49_offline_queue'
const DB_VERSION = 2     // bumped from 1 — added 'queue' store; legacy 'tipping_log' store dropped on upgrade
const STORE      = 'queue'

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION)
    req.onerror   = () => reject(req.error)
    req.onsuccess = () => resolve(req.result)
    req.onupgradeneeded = (ev) => {
      const db = req.result
      // Drop the legacy v1 store if present — entries lost on upgrade are
      // tipping rows from a previous version and should already have synced.
      if (ev.oldVersion < 2 && db.objectStoreNames.contains('tipping_log')) {
        db.deleteObjectStore('tipping_log')
      }
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: 'id' })
        store.createIndex('by_kind',      'kind')
        store.createIndex('by_queued_at', 'queued_at')
      }
    }
  })
}

export async function withStore<T>(mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest | Promise<T>): Promise<T> {
  // A queued entry exists nowhere else until it uploads, so the first write of
  // the session asks the browser not to evict this store (MFP, 30 Sep 2026:
  // scouts' and sprayers' offline captures gone by the next morning).
  if (mode === 'readwrite') requestPersistenceOnce()
  const db = await openDB()
  return new Promise<T>((resolve, reject) => {
    const tx    = db.transaction(STORE, mode)
    const store = tx.objectStore(STORE)
    const req   = fn(store)
    if (req instanceof IDBRequest) {
      // Council T3.9. Settle on the TRANSACTION, not the request. Resolving
      // on req.onsuccess returned before tx.oncomplete committed — if the
      // user navigated / closed the tab in that ~ms gap the tx aborted and
      // the entry was lost despite enqueueX having "succeeded". Capture the
      // request result, then resolve when the transaction actually commits;
      // reject on request error or transaction abort.
      let result: T
      let reqErr: DOMException | null = null
      req.onsuccess = () => { result = req.result as T }
      req.onerror   = () => { reqErr = req.error }
      tx.oncomplete = () => resolve(result)
      tx.onerror    = () => reject(reqErr ?? tx.error)
      tx.onabort    = () => reject(reqErr ?? tx.error ?? new Error('IndexedDB transaction aborted'))
    } else {
      // Promise branch (no callers today) kept defensively.
      Promise.resolve(req).then(resolve, reject)
    }
  })
}

export async function pendingCount(): Promise<number> {
  return withStore('readonly', s => s.count())
}

export interface QueueBreakdown {
  total:          number
  tipping:        number
  cooling_batch:  number
  // Berry flow Tier 3 — cold-tunnel transitions + batch tips (mig 20260730070407).
  cooling_transition: number
  batch_tip:      number
  pallet:         number
  // External-pallet receipts captured at the gate (mig 20260807090000).
  pallet_receipt: number
  scouting:       number
  scout_walk:     number
  farm_problem:   number
  trap_visit:     number
  security_incident: number
  pick:           number
  // Piece-work MONEY drops (kind 'pick_tallies') — distinct from 'pick'. They
  // count into `total` but had no visible slot, so a stuck money-write was
  // invisible in the badge breakdown (scale audit OP-5).
  pick_tallies:   number
  // Packhouse capture scans (kind 'packout_drops') — the packhouse twin of
  // pick_tallies, and a MONEY kind for the same reason: each row is a credit.
  packout_drops:  number
  spray:          number
  spray_complete: number
  reject:         number
  phenology:      number
  fert:           number
  fert_complete:  number
  irrigation:     number
  drain_reading:  number
  qc:             number
  delivery:       number
  fuel:           number
  equipment_service: number
  // Audit must-fix #6 — labour writes that opt into queue support.
  labour_attest:  number
  labour_adjust:  number
  // Phase 2 wave 2e — scan-driven stock writes.
  stock_movement: number
  // STK-32 — counted figures typed on a cycle-count worksheet in the store.
  stock_count_line: number
  // BigHarvest P4.8 — a picker lent to another team for the day. The one
  // harvest kind that is NOT money: the count lands against the pick either
  // way, so a stuck lend costs a crew list, never a bin.
  harvest_team_move: number
  // FM04 UF-09 — a picker lent to a farm crew for the day. Not money.
  farm_day_lend:  number
  // Mig 20261248500000 — bags issued to a packer. Not money.
  piece_bag_issue: number
  // FM07 UF-18 — PPE issued without signal (a signed record; surfaced like money).
  ppe_issue:      number
  // BigHarvest P1b — span clock-ins (MONEY kind; pay-bearing).
  span_clock:     number
  // BigHarvest P4.5 — tractor loads + their delivery notes. Treated as a
  // MONEY kind: a stranded load is a hole in the packhouse reconciliation.
  harvest_load:   number
  harvest_general: number
  // CPB — a wagon submitted to a packhouse. Not money, but a stranded one is a
  // load the packhouse never sees, so it is surfaced like the rest.
  farm_load:      number
  // FM08 UF-26 — a logbook trip and a tractor field operation logged without signal.
  vehicle_trip:   number
  field_operation: number
  // Council T3.8 — entries that drain gave up on (permanent fault / retry
  // cap). They're still in the queue; surface them so the operator knows
  // a write needs manual attention instead of it silently vanishing.
  failed:         number
}

export async function pendingBreakdown(): Promise<QueueBreakdown> {
  const all = await listPending()
  return {
    total:         all.length,
    tipping:       all.filter(e => e.kind === 'tipping').length,
    cooling_batch: all.filter(e => e.kind === 'cooling_batch').length,
    cooling_transition: all.filter(e => e.kind === 'cooling_transition').length,
    batch_tip:     all.filter(e => e.kind === 'batch_tip').length,
    pallet:        all.filter(e => e.kind === 'pallet').length,
    pallet_receipt: all.filter(e => e.kind === 'pallet_receipt').length,
    scouting:      all.filter(e => e.kind === 'scouting').length,
    scout_walk:    all.filter(e => e.kind === 'scout_walk').length,
    farm_problem:  all.filter(e => e.kind === 'farm_problem').length,
    trap_visit:    all.filter(e => e.kind === 'trap_visit').length,
    security_incident: all.filter(e => e.kind === 'security_incident').length,
    pick:          all.filter(e => e.kind === 'pick').length,
    pick_tallies:  all.filter(e => e.kind === 'pick_tallies').length,
    packout_drops: all.filter(e => e.kind === 'packout_drops').length,
    spray:         all.filter(e => e.kind === 'spray').length,
    spray_complete: all.filter(e => e.kind === 'spray_complete').length,
    reject:        all.filter(e => e.kind === 'reject').length,
    phenology:     all.filter(e => e.kind === 'phenology').length,
    fert:          all.filter(e => e.kind === 'fert').length,
    fert_complete: all.filter(e => e.kind === 'fert_complete').length,
    irrigation:    all.filter(e => e.kind === 'irrigation').length,
    drain_reading: all.filter(e => e.kind === 'drain_reading').length,
    qc:            all.filter(e => e.kind === 'qc').length,
    delivery:      all.filter(e => e.kind === 'delivery').length,
    fuel:          all.filter(e => e.kind === 'fuel').length,
    equipment_service: all.filter(e => e.kind === 'equipment_service').length,
    labour_attest: all.filter(e => e.kind === 'labour_attest').length,
    labour_adjust: all.filter(e => e.kind === 'labour_adjust').length,
    stock_movement: all.filter(e => e.kind === 'stock_movement').length,
    stock_count_line: all.filter(e => e.kind === 'stock_count_line').length,
    span_clock:    all.filter(e => e.kind === 'span_clock').length,
    harvest_load:  all.filter(e => e.kind === 'harvest_load').length,
    farm_load:     all.filter(e => e.kind === 'farm_load').length,
    vehicle_trip:  all.filter(e => e.kind === 'vehicle_trip').length,
    field_operation: all.filter(e => e.kind === 'field_operation').length,
    harvest_general: all.filter(e => e.kind === 'harvest_general').length,
    harvest_team_move: all.filter(e => e.kind === 'harvest_team_move').length,
    farm_day_lend: all.filter(e => e.kind === 'farm_day_lend').length,
    piece_bag_issue: all.filter(e => e.kind === 'piece_bag_issue').length,
    ppe_issue:     all.filter(e => e.kind === 'ppe_issue').length,
    failed:        all.filter(e => e.failed).length,
  }
}

export async function listPending(): Promise<Entry[]> {
  return withStore('readonly', s => s.getAll() as IDBRequest<Entry[]>)
}

export async function deleteEntry(id: string): Promise<void> {
  await withStore('readwrite', s => s.delete(id))
}

// Hard attempts = the server answered and rejected. Legacy rows (and every
// row queued before this field existed) read as 0, which is the right default:
// a fresh give-up budget, not an inherited one.
export function hardAttempts(entry: Entry): number {
  return entry.hard_attempts ?? 0
}

// `hard` marks a failure the SERVER produced (a rejection that says something
// about the payload or the caller), as opposed to a network blip / 5xx / expired
// JWT, which says nothing and must not spend the give-up budget. Callers that
// know the difference pass it; the default is the conservative one.
export async function recordFailure(entry: Entry, err: Error, opts?: { hard?: boolean }): Promise<void> {
  entry.attempt_count += 1
  if (opts?.hard) entry.hard_attempts = hardAttempts(entry) + 1
  entry.attempted_at   = new Date().toISOString()
  entry.last_error     = err.message
  await withStore('readwrite', s => s.put(entry))
}

// Council T3.8. Mark an entry as permanently failed but KEEP it — never
// delete on a non-network error. The operator can see + triage failed
// rows via the offline-queue badge; future drains skip them.
export async function markFailed(entry: Entry, err: Error): Promise<void> {
  entry.attempt_count += 1
  entry.hard_attempts  = hardAttempts(entry) + 1   // giving up is by definition a hard failure
  entry.attempted_at   = new Date().toISOString()
  entry.last_error     = err.message
  entry.failed         = true
  await withStore('readwrite', s => s.put(entry))
}
